/** T83 — Services CRUD client. */
import { apiClient } from "./client";

/** Operational service type (Loại dịch vụ) — orthogonal to the clinical category. */
export type ServiceType = "EXAM" | "SERVICE";

export const SERVICE_TYPES: { code: ServiceType; label: string }[] = [
  { code: "EXAM", label: "Khám bệnh" },
  { code: "SERVICE", label: "Dịch vụ / thủ thuật" },
];

export function serviceTypeLabel(code: ServiceType | null): string {
  if (!code) return "—";
  return SERVICE_TYPES.find((t) => t.code === code)?.label ?? code;
}

/** VAT category (Loại thuế suất) — parity with the external-HIS taxtype filter. */
export type VatCategory = "KCT" | "VAT_0" | "VAT_5" | "VAT_8" | "VAT_10";

export const VAT_CATEGORIES: { code: VatCategory; label: string }[] = [
  { code: "KCT", label: "Không chịu thuế (KCT)" },
  { code: "VAT_0", label: "0%" },
  { code: "VAT_5", label: "5%" },
  { code: "VAT_8", label: "8%" },
  { code: "VAT_10", label: "10%" },
];

export function vatLabel(code: VatCategory | null): string {
  if (!code) return "—";
  return VAT_CATEGORIES.find((v) => v.code === code)?.label ?? code;
}

export interface ClinicServiceItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  priceVnd: number | null;
  costPriceVnd: number | null;
  vatCategory: VatCategory | null;
  serviceType: ServiceType;
  sortOrder: number;
  durationMinutes: number | null;
  /** Owning khoa (Department) id — nullable ("— Không thuộc khoa —"). */
  departmentId: string | null;
  active: boolean;
}

export interface ServiceCreateInput {
  code: string;
  name: string;
  description?: string | null;
  category?: string | null;
  priceVnd?: number | null;
  costPriceVnd?: number | null;
  vatCategory?: VatCategory | null;
  serviceType?: ServiceType | null;
  sortOrder?: number | null;
  durationMinutes?: number | null;
  departmentId?: string | null;
}

export interface ServiceUpdateInput {
  name: string;
  description?: string | null;
  category?: string | null;
  priceVnd?: number | null;
  costPriceVnd?: number | null;
  vatCategory?: VatCategory | null;
  serviceType?: ServiceType | null;
  sortOrder?: number | null;
  durationMinutes?: number | null;
  departmentId?: string | null;
}

/**
 * Curated Vietnamese categories for the small/medium phòng khám (Lite scope).
 * Customers can type a custom category in the form; this list just primes the
 * dropdown and keeps the catalogue tidy across tenants.
 */
export const SERVICE_CATEGORIES: { code: string; label: string }[] = [
  { code: "KHAM", label: "Khám bệnh" },
  { code: "XET-NGHIEM", label: "Xét nghiệm" },
  { code: "CHAN-DOAN-HINH-ANH", label: "Chẩn đoán hình ảnh" },
  { code: "THU-THUAT", label: "Thủ thuật y tế" },
  { code: "TIEM-CHUNG", label: "Tiêm chủng" },
  { code: "NHA-KHOA", label: "Nha khoa" },
  { code: "SAN-PHU-KHOA", label: "Sản phụ khoa" },
  { code: "NHI-KHOA", label: "Nhi khoa" },
  { code: "VAT-LY-TRI-LIEU", label: "Vật lý trị liệu" },
  { code: "KHAC", label: "Khác" },
];

export function categoryLabel(code: string | null): string {
  if (!code) return "(chưa phân loại)";
  return SERVICE_CATEGORIES.find((c) => c.code === code)?.label ?? code;
}

export const servicesApi = {
  listAll(): Promise<ClinicServiceItem[]> {
    return apiClient
      .get<ClinicServiceItem[]>("/v1/services/all")
      .then((r) => r.data);
  },
  create(req: ServiceCreateInput): Promise<ClinicServiceItem> {
    return apiClient
      .post<ClinicServiceItem>("/v1/services", req)
      .then((r) => r.data);
  },
  update(id: string, req: ServiceUpdateInput): Promise<ClinicServiceItem> {
    return apiClient
      .put<ClinicServiceItem>(`/v1/services/${id}`, req)
      .then((r) => r.data);
  },
  disable(id: string): Promise<ClinicServiceItem> {
    return apiClient
      .post<ClinicServiceItem>(`/v1/services/${id}/disable`)
      .then((r) => r.data);
  },
  enable(id: string): Promise<ClinicServiceItem> {
    return apiClient
      .post<ClinicServiceItem>(`/v1/services/${id}/enable`)
      .then((r) => r.data);
  },
  /** Hard delete (permanent). 409 nếu dịch vụ đang được tham chiếu. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/services/${id}/permanent`).then(() => undefined);
  },
};

export const servicesQueryKeys = {
  all: () => ["services", "all"] as const,
};
